import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";

import BrokerAssetCounts from "./BrokerAssetCounts";
import { LAYERS } from "./constants";
import { formatDate, formatMoney } from "./helpers";
import { brokerStatsById, brokersById, claimsByMotor, motorsById, propertiesById } from "./portfolioData";

export function MarkerHover({ hoverRef }) {
  const [hover, setHover] = useState(null);

  useEffect(() => {
    hoverRef.current = (next) => {
      setHover((current) => {
        if (!next) return current ? null : current;
        if (current?.entityId === next.entityId && current?.entityType === next.entityType) {
          if (current.x === next.x && current.y === next.y) return current;
          return { ...current, x: next.x, y: next.y };
        }
        return next;
      });
    };
    return () => {
      hoverRef.current = null;
    };
  }, [hoverRef]);

  if (!hover) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        left: hover.x,
        top: hover.y,
        transform: "translate(-50%, calc(-100% - 14px))",
        zIndex: 3,
        pointerEvents: "none",
        background: "#fff",
        borderRadius: 1,
        boxShadow: 3,
        px: 1.25,
        py: 0.75,
      }}
    >
      <MarkerPopup entityType={hover.entityType} entityId={hover.entityId} />
    </Box>
  );
}

function MarkerPopup({ entityType, entityId }) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || "en";

  if (entityType === LAYERS.broker) {
    const broker = brokersById[entityId];
    const stats = brokerStatsById[entityId];
    if (!broker || !stats) return null;
    return (
      <Box sx={{ minWidth: 160 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {broker.name}
        </Typography>
        <Typography variant="caption" component="div">
          {broker.city} · {broker.manager_name}
        </Typography>
        <Typography variant="caption" component="div">
          <BrokerAssetCounts stats={stats} />
        </Typography>
        <Typography variant="caption" component="div">
          {t("portfolioExplorer.card.totalInsured")} {formatMoney(stats.totalInsured, language)} · {t("portfolioExplorer.fields.premium")} {formatMoney(stats.totalPremium, language)}
        </Typography>
      </Box>
    );
  }

  if (entityType === "accident") {
    const motor = motorsById[entityId];
    const claim = (claimsByMotor[entityId] || [])[0];
    if (!motor || !claim) return null;
    return (
      <Box sx={{ minWidth: 180, maxWidth: 240 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {motor.registration_number}
        </Typography>
        <Typography variant="caption" component="div">
          {t("portfolioExplorer.claims.accident")} · {t(`portfolioExplorer.enums.cause.${claim.cause}`)}
        </Typography>
        <Typography variant="caption" component="div">
          {claim.accident_city} · {formatDate(claim.loss_date, language)}
        </Typography>
        <Typography variant="caption" component="div">
          {t("portfolioExplorer.claims.claimed")} {formatMoney(claim.claimed_amount, language)}
        </Typography>
      </Box>
    );
  }

  if (entityType === LAYERS.property) {
    const property = propertiesById[entityId];
    if (!property) return null;
    return (
      <Box sx={{ minWidth: 180, maxWidth: 240 }}>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {property.address}
        </Typography>
        <Typography variant="caption" component="div">
          {t(`portfolioExplorer.enums.propertyType.${property.property_type}`)} · {property.city}
        </Typography>
        <Typography variant="caption" component="div">
          {t("portfolioExplorer.card.totalInsured")} {formatMoney(property.total_insured_value, language)} · {t("portfolioExplorer.fields.premium")} {formatMoney(property.premium, language)}
        </Typography>
      </Box>
    );
  }

  const motor = motorsById[entityId];
  if (!motor) return null;
  return (
    <Box sx={{ minWidth: 160 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {motor.registration_number}
      </Typography>
      <Typography variant="caption" component="div">
        {motor.make} {motor.model} · {t(`portfolioExplorer.enums.vehicleType.${motor.vehicle_type}`)}
      </Typography>
      <Typography variant="caption" component="div">
        {motor.city} · {t("portfolioExplorer.fields.vehicleValue")} {formatMoney(motor.vehicle_value, language)} · {t("portfolioExplorer.fields.premium")} {formatMoney(motor.premium, language)}
      </Typography>
    </Box>
  );
}

export default MarkerPopup;
